import actionConsts from 'constants/actions';
import { getUniqueItemsOnly } from 'util/functions.js'

const initialState = {
	twoDaysRestrictedActionsList: [],
	threeDaysRestrictedActionsList: [],
	fiveDaysRestrictedActionsList: [],
	sevenDaysRestrictedActionsList: [],
	tenDaysRestrictedActionsList: [],
	fifteenDaysRestrictedActionsList: [],
	twentyDaysRestrictedActionsList: [],
	permanentDisabledActionList: [],
	disabledActionActorObj: {},
};

const findActionIdOfOption = (actionOptionId, isDelayedAction, actionOptionsList) => {
	for(let i = 0 ; i < actionOptionsList.length; i++){
		if(actionOptionsList[i].id == actionOptionId){
			const actionId = actionOptionsList[i].actionId;
			// For delayed actions (Action 7 option 18, Action 8 option 19),
			// when isDelayedAction=true, this is the response - don't count it as a new action attempt
			// Only Action 7 and Action 8 have delayed_duration > 0 in the new system
			if([7, 8].includes(actionId) && isDelayedAction){
				// This is a delayed action response, return -1 to prevent double-counting
				return -1;
			}
			return actionId;
		}
	}
	return -1;
}

// Build restriction mappings from SQL data
// Maps action IDs to their restriction configurations
const buildActionRestrictions = () => {
	return {
		// Permanent disable (number_of_times)
		// Maps action ID to max number of successful attempts allowed
		// Based on action_option_action_restrictions where action_option_id = dependent_action_option_id
		maxTimes: {
			1: 3,  // Action 1 (option 1): max 9 times
			8: 3,  // Action 8 (option 19): max 3 times
			18: 4,  // Action 18 (option 43): max 4 times
			20: 5,  // Action 20 (option 47): max 5 times
		},
		// Time-based actionRestrictions (number_of_days)
		// Maps action ID to number of days to wait
		daysWait: {
			2: 10,   // Action 2 (option 2): 10 days
			3: 5,    // Action 3 (option 5): 5 days
			4: 5,    // Action 4 (options 6-9): 5 days
			5: 3,    // Action 5 (options 10-13): 3 days
			6: 20,   // Action 6 (options 14-17): 20 days
			7: 3,    // Action 7 (option 18): 3 days
			8: 15,   // Action 8 (option 19): 15 days
			10: 2,   // Action 10 (option 24): 2 days
			11: 5,   // Action 11 (options 25-27): 5 days
			12: 10,  // Action 12 (options 28-31): 10 days
			14: 5,   // Action 14 (option 33): 5 days
			15: 7,   // Action 15 (options 34-37): 7 days
			16: 5,   // Action 16 (option 38): 5 days
			18: 10,  // Action 18 (option 43): 10 days (also has max 4 times)
			19: 5,   // Action 19 (options 44-46): 5 days
			21: 5,   // Action 21 (option 48): 5 days
		},
		// Per-actor actionRestrictions (number_of_times_per_actor)
		// Actions that have per-actor limits (handled in UPDATE_ACTIONS_MAXEDOUT)
		perActorMax: {
			2: 3,   // Action 2 (option 2): 3 times per actor
			4: 4,   // Action 4 (options 6-9): 4 times per actor
			5: 3,   // Action 5 (options 10-13): 3 times per actor
			9: 4,   // Action 9 (options 20-23): 4 times per actor
			17: 4,  // Action 17 (options 39-42): 4 times per actor
		}
	};
}

const calculateActionFrequency = (payload) => {
	let actionOptionsTakenList = payload.actionOptionsTakenList;
	actionOptionsTakenList.sort((a, b) => {
		return a.day - b.day;
	});
	let actionOptionsList = payload.actionOptionsList;
	let actionFrequency = {};
	let actionFrequencySuccessful = {}; // Track successful attempts only for specific actions
	let actionLastTakenDay = {};
	
	// Group actions by actionID and day for proper counting (backend groups by day)
	const actionsByActionIdAndDay = {};
	
	for (let eachActionOptionTaken = 0; eachActionOptionTaken < actionOptionsTakenList.length; eachActionOptionTaken++) {
		const takenAction = actionOptionsTakenList[eachActionOptionTaken];
		let actionID = findActionIdOfOption(
			takenAction.actionOptionId, 
			takenAction.isDelayedAction, 
			actionOptionsList
		);
		
		// Skip if actionID is -1 (delayed action response that shouldn't be counted)
		if(actionID === -1){
			continue;
		}

		//Creating and populating actionFrequency (all attempts)
		if(actionFrequency[actionID]){
			actionFrequency[actionID] = actionFrequency[actionID] + 1;
		}else{
			actionFrequency[actionID] = 1;
		}
		
		// Group by actionID and day for successful attempts
		// Backend groups by day first, then filters for successful actions
		// So we need to track unique days with successful attempts per action
		if(takenAction.isActionSuccessful !== undefined && takenAction.isActionSuccessful){
			const dayKey = `${actionID}_${takenAction.day}`;
			if(!actionsByActionIdAndDay[dayKey]){
				actionsByActionIdAndDay[dayKey] = true;
				// Count unique days with successful attempts (matching backend logic)
				// Backend: groupBy('day') then filter(isActionSuccessful) then count length
				if(actionFrequencySuccessful[actionID]){
					actionFrequencySuccessful[actionID] = actionFrequencySuccessful[actionID] + 1;
				}else{
					actionFrequencySuccessful[actionID] = 1;
				}
			}
		}

		//Creating and populating actionLastTakenDay
		if(!actionLastTakenDay[actionID]){
			actionLastTakenDay[actionID] = takenAction.day;
		}else if(actionLastTakenDay[actionID] < takenAction.day){
			actionLastTakenDay[actionID] = takenAction.day;
		}

	}
	return { actionFrequency, actionFrequencySuccessful, actionLastTakenDay }
}

const calculateIsSuccessDisabling = (payload) => {
	let actionOptionsTakenList = payload.actionOptionsTakenList;
	actionOptionsTakenList.sort((a, b) => {
		return a.day - b.day;
	});
	let actionActorToDisable;

	// This function is kept for future use if success-based disabling is needed
	// Currently, actionRestrictions are handled via UPDATE_RESTRICTIONS and UPDATE_ACTIONS_MAXEDOUT

	return actionActorToDisable;
}

const updateDisabledActors = (disabledActionActorObj, actionId, actorsSelected) => {
	if(disabledActionActorObj[actionId]){
		disabledActionActorObj[actionId].push(actorsSelected[0]);
	}else{
		disabledActionActorObj[actionId] = actorsSelected;
	}

	return disabledActionActorObj;
}

const updateDisabledActorsWithBadge = (disabledActionActorObj, actionId, actorsSelected) => {
	disabledActionActorObj[actionId] = actorsSelected;
	return disabledActionActorObj;
}

const updateDisabledSuccessfulCondition = (disabledActionActorObj, actionActorToDisable) => {
	for (let keys in actionActorToDisable) {
		disabledActionActorObj[keys] = actionActorToDisable[keys];
	}
	return disabledActionActorObj;
}

const restrictions = (state = initialState, action = {}) => {
	switch (action.type) {
		case 'SET_INIT_DATA':{
			return {
				...state,
				disabledActionActorObj: action.payload.reduxData["DISABLE_ACTION_ACTOR"] || state.disabledActionActorObj
			};
		}
		case 'UPDATE_RESTRICTIONS':{
			let currentDay = action.payload.currentDay;
			const { actionFrequencySuccessful, actionLastTakenDay } = calculateActionFrequency(action.payload);
			let actionMaxedOut = [];
			let actionTemp2Restricted = [];
			let actionTemp3Restricted = [];
			let actionTemp5Restricted = [];
			let actionTemp7Restricted = [];
			let actionTemp10Restricted = [];
			let actionTemp15Restricted = [];
			let actionTemp20Restricted = [];

			const actionRestrictions = buildActionRestrictions();

			// Check count-based actionRestrictions (permanent disable)
			// Backend checks isActionSuccessful for number_of_times actionRestrictions, so use actionFrequencySuccessful
			for(const actions in actionFrequencySuccessful){
				const actionId = parseInt(actions, 10);
				if(actionRestrictions.maxTimes[actionId] && actionFrequencySuccessful[actions] >= actionRestrictions.maxTimes[actionId]){
					actionMaxedOut.push(actionId);
				}
			}

			// Per-actor limits (perActorMax) are NOT handled here
			// They are correctly handled via disabledActionActorObj in the UPDATE_ACTIONS_MAXEDOUT case
			// This allows actions to remain accessible for actors who haven't reached their limit
			// The previous code incorrectly treated perActorMax as total action limits, blocking entire actions
			// when only some actors reached their limit. This has been removed to fix the issue.
			//
			// REMOVED: Incorrect perActorMax check that blocked entire actions
			// for(const actions in actionFrequencySuccessful){
			// 	const actionId = parseInt(actions, 10);
			// 	const perActorMax = actionRestrictions.perActorMax[actionId];
			// 	if(perActorMax && actionFrequencySuccessful[actions] >= perActorMax){
			// 		if(!actionMaxedOut.includes(actionId)){
			// 			actionMaxedOut.push(actionId);
			// 		}
			// 	}
			// }

			// Check time-based actionRestrictions for actions that have been taken
			for(const actions in actionLastTakenDay){
				const actionId = parseInt(actions, 10);
				const daysSinceLastTaken = currentDay - actionLastTakenDay[actions];
				const daysWait = actionRestrictions.daysWait[actionId];
				
				if(daysWait && daysSinceLastTaken < daysWait){
					// Add to appropriate restriction list based on days
					if(daysWait === 2){
						actionTemp2Restricted.push(actionId);
					} else if(daysWait === 3){
						actionTemp3Restricted.push(actionId);
					} else if(daysWait === 5){
						actionTemp5Restricted.push(actionId);
					} else if(daysWait === 7){
						actionTemp7Restricted.push(actionId);
					} else if(daysWait === 10){
						actionTemp10Restricted.push(actionId);
					} else if(daysWait === 15){
						actionTemp15Restricted.push(actionId);
					} else if(daysWait === 20){
						actionTemp20Restricted.push(actionId);
					}
				}
			}	

			// Replace permanentDisabledActionList with newly calculated restrictions
			// This ensures actions are only disabled when they actually meet the limit criteria
			// (not permanently disabled from previous calculations)

			return{
				...state,
				permanentDisabledActionList: [...actionMaxedOut],
				twoDaysRestrictedActionsList: [...actionTemp2Restricted],
				threeDaysRestrictedActionsList: [...actionTemp3Restricted],
				fiveDaysRestrictedActionsList: [...actionTemp5Restricted],
				sevenDaysRestrictedActionsList: [...actionTemp7Restricted],
				tenDaysRestrictedActionsList: [...actionTemp10Restricted],
				fifteenDaysRestrictedActionsList: [...actionTemp15Restricted],
				twentyDaysRestrictedActionsList: [...actionTemp20Restricted],
			};
		}

		case 'UPDATE_ACTIONS_MAXEDOUT':{
			let disabledActors = action.payload.disabledActors;
			let actionMaxedOut = [];
			const actionRestrictions = buildActionRestrictions();

			for(const actions in disabledActors){
				const actionId = parseInt(actions, 10);
				const perActorMax = actionRestrictions.perActorMax[actionId];

				// Check if action has per-actor restrictions
				if(perActorMax){
					// For per-actor restrictions, we rely on actor-level disabling (disabledActionActorObj)
					// which already prevents selecting maxed-out actors.
					// The backend only rejects when ALL selected actors are maxed out.
					//
					// We only disable the entire action if we have a very high number of disabled actors
					// (>= 10), which suggests all available actors are likely maxed out.
					// This is a conservative heuristic since we don't have the total actor count readily available.
					//
					// Note: Actor-specific disabling already works via disabledActionActorObj,
					// so users can still take the action on non-maxed actors even if some are disabled.
					const disabledActorCount = disabledActors[actions].length;

					// Only disable entire action if we're very confident all actors are maxed
					// (high threshold to avoid false positives)
					if(disabledActorCount >= 10){
						actionMaxedOut.push(actionId);
					}
				}
			}

			let newMaxedOut = getUniqueItemsOnly(actionMaxedOut, state.permanentDisabledActionList)

			return{
				...state,
				permanentDisabledActionList: [...state.permanentDisabledActionList,...newMaxedOut]
			}
		}

		case 'UPDATE_DISABLED_ACTORS_NO_CONDITION':{
			let actorsSelected = action.payload.actorsSelected;
			let actionId = action.payload.actionId;
			return{
				...state,
				disabledActionActorObj: updateDisabledActors(state.disabledActionActorObj, actionId, actorsSelected)
			};
		}

		case 'UPDATE_DISABLED_ACTORS_BUILD_NETWORK':{
			let actorsSelected = action.payload.actorsSelected;
			let actionId = action.payload.actionId;
			return{
				...state,
				disabledActionActorObj: updateDisabledActorsWithBadge(state.disabledActionActorObj, actionId, actorsSelected)
			};
		} 

		case 'UPDATE_DISABLED_ACTORS_RECOGNITION':{
			let actorsSelected = action.payload.actorsSelected;
			let actionId = action.payload.actionId;
			return{
				...state,
				disabledActionActorObj: updateDisabledActorsWithBadge(state.disabledActionActorObj, actionId, actorsSelected)
			};
		}

		case 'UPDATE_DISABLED_ACTORS_SUCCESSFUL_CONDITION':{
			let actionActorToDisable = calculateIsSuccessDisabling(action.payload);
			return{
				...state,
				disabledActionActorObj: updateDisabledSuccessfulCondition(state.disabledActionActorObj, actionActorToDisable)
			}
		}

		default:
			return state;
	}
};

export default restrictions;